import { memo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AcceptType,
  CentralModalType,
  DisclaimerType as DiscType,
  DisclaimerTypeHelper,
  ErrorType,
  FATCA,
  RiskProfileErrors,
  SecondaryError,
} from "../../const";
import PDPA from "./PDPA";
import FXRisk from "./FXRisk";
import HighRisk from "./HighRisk";
import HNW from "./HNW";
import RiskProfile from "./RiskProfile";
import Error from "./Error";
import { closeModal, closeSubModal } from "redux/reducers/central-modal";
import AcceptFactsheet from "./AcceptFactsheet";
import SubscriptionProblemModal from "./SubscriptionProblemModal";
import NoneHNW from "./NoneHNW";
import PWMRegister from "./PWMRegister";
import PWMRegisterWaitApproved from "./PWMRegisterWaitApproved";
import AcceptRiskDesc from "./AcceptRiskDesc";
import { ModalSelector } from "redux/selector/modals-selector";
import SecondMarketHighRisk from "./SecondMarketHighRisk";
import AcceptPDPAComp from "./AcceptPDPAComp";
import { useIntl } from "react-intl";
import AcceptUnitAvaliable from "./AcceptUnitAvaliable";
import FormFatca from "./FormFatca";
import { renderSpecialUhnwModal } from "./renderSpecialUhnwModal";
import SystemIsNotReadyModel from "./SystemIsNotReadyModel";
import NoSecuritiesFoundError from "./NoSecuritiesFoundError";
import ConfirmHnwSelfDeclare from "./CentralSubModal/ConfirmHnwSelfDeclare";
import SystemClose from "./SystemClose";
import AmendHNW from "./AmendHNW";
import NoneHnwContactBranch from "./NoneHnwContactBranch";
import FutureRisk from "./FutureRisk";
import OffshoreTermAndConditions from "./OffshoreTermAndConditions";
import ValidateDepositAccount from "./ValidateDepositAccount";
import BondConnectPDPA from "./BondConnectPDPA";
import OffshoreNonFaceCRS from "./OffshoreNonFaceCRS";
import OffshoreInformPopups from "./OffshoreInformPopups";

const CentralModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { code, messageId, actions, state, error } = useSelector(ModalSelector.centralModalState);
  const subModalState = useSelector(ModalSelector.centralSubModalState);

  //* temporary hide main modal when sub-modal required
  useEffect(() => {
    const mainModals = document.querySelectorAll("div.ant-modal:not(.sub-modal)");
    const action = subModalState.code ? "add" : "remove";

    mainModals.forEach((elem) => elem.classList[action]("d-none"));
  }, [subModalState.code]);

  const close = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const closeSub = useCallback(() => {
    dispatch(closeSubModal());
  }, [dispatch]);

  const modalProps = {
    code,
    messageId,
    close,
    actions,
    state,
    centered: true,
    closable: false,
    maskClosable: false,
    footer: null,
    visible: true,
  };

  const subModalProps = {
    className: "sub-modal",
    code: subModalState.code,
    messageId: subModalState.messageId,
    close: closeSub,
    actions: subModalState.actions,
    state: subModalState.state,
    centered: true,
    closable: false,
    maskClosable: false,
    footer: null,
    visible: true,
  };

  function _render(Component, key, isSubModal) {
    const $code = isSubModal ? subModalState.code : code;
    const $modalProps = isSubModal ? subModalProps : modalProps;
    return <Component {...$modalProps} key={key} visible={$code === key} />;
  }

  function renderModals() {
    return [
      <RiskProfile {...modalProps} key="ERROR-RISK-PROFILE" visible={isErrorRiskProfile(code)} />,
      <SubscriptionProblemModal {...modalProps} key="SUBSCRIPTION-ERROR" visible={isSubscriptionProblem(code)} />,
      <Error {...modalProps} key="ERROR-MODAL" visible={error && !modalErrorExceptionList(code)} />,
      ...renderSpecialUhnwModal({ dispatch, intl, code, modalProps }),
      _render(HighRisk, DiscType.HighRisk),
      _render(FXRisk, DiscType.FXRisk),
      _render(FutureRisk, DiscType.FutureRisk),
      _render(HNW, DiscType.HNW),
      _render(AmendHNW, DiscType.AmendHNW),
      _render(AmendHNW, DiscType.Amend2ndHNW),
      _render(NoneHnwContactBranch, ErrorType.NONE_HNW_CONTACT_BRANCH),
      _render(PDPA, DiscType.PDPA),
      _render(AcceptFactsheet, AcceptType.FactsheetCustomMessage),
      _render(NoneHNW, DiscType.NoneHNW),
      _render(PWMRegister, AcceptType.PWMRegister),
      _render(PWMRegisterWaitApproved, AcceptType.PwmRegisterInfo),
      _render(AcceptRiskDesc, AcceptType.RiskDesc),
      _render(SecondMarketHighRisk, AcceptType.SecondMarketInvestment),
      _render(AcceptPDPAComp, DiscType.PDPA_COMP),
      _render(AcceptUnitAvaliable, AcceptType.UnitAvaliable),
      _render(FormFatca, FATCA.NO_DATA),
      _render(SystemIsNotReadyModel, RiskProfileErrors.SystemNotReady),
      _render(NoSecuritiesFoundError, SecondaryError.NoSecuritiesFoundInPort),
      _render(ConfirmHnwSelfDeclare, DisclaimerTypeHelper.ConfirmSelfDeclare, true),
      _render(SystemClose, ErrorType.SystemClose),
      _render(OffshoreTermAndConditions, DiscType.OffshoreTermAndConditions),
      _render(ValidateDepositAccount, CentralModalType.ValidateDepositAccount),
      _render(BondConnectPDPA, DiscType.BondConnectPDPA),
      _render(OffshoreNonFaceCRS, AcceptType.OffshoreNonFaceCRS),
      _render(OffshoreInformPopups, CentralModalType.OffshoreInformPopups),
    ];
  }

  if (!code && !error) return null;
  return renderModals();
};

export default memo(CentralModal);

function isErrorRiskProfile(code) {
  return [ErrorType.AGE_OVER_60, ErrorType.ATTITUDE, ErrorType.EXPERIENCE].includes(code);
}

function isSubscriptionProblem(code) {
  return [
    ErrorType.SETTLE_FULLY_PURCHASE,
    ErrorType.SETTLE_REMAIN_NOT_ENOUGH,
    ErrorType.CBS_FULLY_PURCHASE,
    ErrorType.CBS_REMAIN_NOT_ENOUGH,
    ErrorType.REF_PRICING_NOT_MATCH,
    ErrorType.CHANNAL_STATUS_NOT_MATCH,
  ].includes(code);
}

function modalErrorExceptionList(code) {
  return [
    ErrorType.AGE_OVER_60,
    ErrorType.ATTITUDE,
    ErrorType.EXPERIENCE,
    ErrorType.SystemClose,
    ErrorType.NONE_HNW_CONTACT_BRANCH,
  ].includes(code);
}
